import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { findRef, findBulk, exportFind } from "@/lib/procureApi";
import { STAGE, STAGE_ORDER, fmtDate, fmtInt } from "@/lib/format";
import { MagnifyingGlass, Path, ArrowRight, Spinner, DownloadSimple, Rows, TextT } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_OPTIONS = [
  { value: "any", label: "Any field" },
  { value: "ref2", label: "Reference 2" },
  { value: "order", label: "Order #" },
  { value: "enduser", label: "End user" },
  { value: "OR", label: "OR" },
  { value: "OQ", label: "OQ" },
  { value: "OF", label: "OF" },
  { value: "OI", label: "OI" },
  { value: "OX", label: "OX" },
  { value: "OS", label: "OS" },
  { value: "OJ", label: "OJ" },
];

const StageBlock = ({ stage, entries }) => {
  const s = STAGE[stage] || { name: stage, color: "#94A3B8" };
  const has = entries && entries.length > 0;
  return (
    <div className={`min-w-[150px] flex-1 border p-2.5 ${has ? "border-[#E3E7EC] bg-white" : "border-dashed border-[#E3E7EC] bg-[#F9FAFB]"}`}>
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-7 items-center justify-center rounded-sm font-mono text-[11px] font-semibold text-white" style={{ background: has ? s.color : "#CAD1DB" }}>
          {stage}
        </span>
      </div>
      {has ? (
        <div className="mt-1.5 space-y-1">
          {entries.map((e, i) => (
            <div key={i} className="rounded-sm bg-[#F9FAFB] px-2 py-1 font-mono text-[10px] text-[#64748B]">
              <div className="text-[#0F172A]">#{e.order_number}{e.line_number ? `-${e.line_number}` : ""}</div>
              <div>{fmtDate(e.order_date)}</div>
              {e.buyer && <div className="truncate">{e.buyer}</div>}
              {e.supplier && <div className="truncate" title={e.supplier}>{e.supplier}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-1.5 text-[10px] text-[#9CA3AF]">Not seen</div>
      )}
    </div>
  );
};

const ChainCard = ({ m, order }) => (
  <div className="border border-[#E3E7EC] bg-white p-4" data-testid={`finder-chain-${m.reference2 || "noref"}`}>
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <div className="pf-label">Reference 2</div>
        <div className="font-mono text-xl font-semibold text-[#F0323E]">{m.reference2 || "— none —"}</div>
      </div>
      <div className="text-right">
        <div className="pf-label">Buyer</div>
        <div className="text-sm font-medium text-[#0F172A]">{m.buyer || "Unassigned"}</div>
      </div>
    </div>
    {m.supplier && <p className="mb-3 truncate text-xs text-[#64748B]">{m.supplier}</p>}
    {m.description && <p className="mb-3 truncate text-xs text-[#64748B]">{m.description}</p>}
    <div className="flex flex-wrap items-stretch gap-2">
      {order.map((ot, i) => (
        <div key={ot} className="flex items-stretch gap-2">
          <StageBlock stage={ot} entries={m.stage_chain?.[ot]} />
          {i < order.length - 1 && (
            <div className="flex items-center text-[#CAD1DB]"><ArrowRight size={13} weight="bold" /></div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const parseBulk = (text) => {
  const seen = new Set();
  const out = [];
  for (const raw of (text || "").split(/[\n,;\t]+/)) {
    const v = raw.trim();
    if (v && !seen.has(v)) { seen.add(v); out.push(v); }
  }
  return out;
};

export const Finder = ({ snapshotId, onOpenRef2 }) => {
  const [mode, setMode] = useState("single");
  const [q, setQ] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [field, setField] = useState("any");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);

  const queries = mode === "bulk" ? parseBulk(bulkText) : (q.trim() ? [q.trim()] : []);

  const run = async () => {
    if (!queries.length) return;
    setBusy(true);
    try {
      if (mode === "bulk") {
        const res = await findBulk(queries, field);
        if (!res.count) { toast.error(`No matches for the ${queries.length} value(s) entered`); setResults(null); return; }
        setResults({ ...res, query: `${queries.length} values` });
        setOpen(true);
      } else {
        const res = await findRef(queries[0], snapshotId, field);
        if (!res.count) {
          const label = FIELD_OPTIONS.find((f) => f.value === field)?.label || "value";
          const msg =
            field === "enduser" ? `No end user found matching "${queries[0]}"`
            : field === "any" ? `No match for "${queries[0]}"`
            : `No exact match for ${label} "${queries[0]}"`;
          toast.error(msg);
          setResults(null);
          return;
        }
        setResults(res);
        setOpen(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const doExport = async () => {
    if (!queries.length) { toast.error("Enter at least one value to export"); return; }
    setExporting(true);
    try {
      await exportFind(queries, field);
      toast.success(`Exported ${queries.length} lookup value(s) to Excel`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const order = results?.stage_order?.length ? results.stage_order : STAGE_ORDER;

  return (
    <div className="border border-[#E3E7EC] bg-white p-4" data-testid="finder">
      <div className="mb-3 flex items-center gap-2">
        <Path size={18} weight="duotone" className="shrink-0 text-[#F0323E]" />
        <div className="flex rounded-sm border border-[#E3E7EC] p-0.5" data-testid="finder-mode-toggle">
          {[["single", "Single", TextT], ["bulk", "Bulk", Rows]].map(([k, l, Icon]) => (
            <button
              key={k}
              data-testid={`finder-mode-${k}`}
              onClick={() => setMode(k)}
              className={`flex items-center gap-1.5 rounded-sm px-3 py-1 text-xs font-medium transition-colors ${mode === k ? "bg-[#F0323E] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}
            >
              <Icon size={13} weight="bold" /> {l}
            </button>
          ))}
        </div>
        <span className="pf-label">{mode === "bulk" ? "Paste many values — one per line or comma separated" : "Look up one value"}</span>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <select
          data-testid="finder-field"
          value={field}
          onChange={(e) => setField(e.target.value)}
          className="h-11 shrink-0 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-3 text-sm font-medium text-[#0F172A] outline-none focus:border-[#F0323E] focus:bg-white"
        >
          {FIELD_OPTIONS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        {mode === "bulk" ? (
          <textarea
            data-testid="finder-bulk-input"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={3}
            placeholder={"Paste values, e.g.\n134187\n46584\n138353"}
            className="min-w-[220px] flex-1 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-3 py-2 font-mono text-sm text-[#0F172A] outline-none placeholder:text-[#9CA3AF] focus:border-[#F0323E] focus:bg-white"
          />
        ) : (
          <div className="relative min-w-[220px] flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              data-testid="finder-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder={field === "any"
                ? "Punch in a Reference 2 or any order number — see the full chain, buyer & dates"
                : field === "enduser"
                ? "Type an end-user name (partial is fine) — returns their order chains"
                : `Exact ${FIELD_OPTIONS.find((f) => f.value === field)?.label} — returns the full OR → OQ → OF chain`}
              className="h-11 w-full rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] pl-9 pr-3 text-sm text-[#0F172A] outline-none placeholder:text-[#9CA3AF] focus:border-[#F0323E] focus:bg-white"
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            data-testid="finder-search"
            onClick={run}
            disabled={busy || !queries.length}
            className="flex h-11 items-center gap-2 rounded-sm bg-[#F0323E] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#d92834] disabled:opacity-50"
          >
            {busy ? <Spinner size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="bold" />}
            Find{mode === "bulk" && queries.length ? ` (${queries.length})` : ""}
          </button>
          <button
            data-testid="finder-export"
            onClick={doExport}
            disabled={exporting || !queries.length}
            title="Export lookup results to Excel"
            className="flex h-11 items-center gap-2 rounded-sm border border-[#CAD1DB] bg-white px-4 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-[#EDEFF3] disabled:opacity-50"
          >
            {exporting ? <Spinner size={16} className="animate-spin" /> : <DownloadSimple size={16} weight="bold" />}
            Excel
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="finder-results" className="max-h-[85vh] max-w-5xl overflow-hidden border-[#E3E7EC] bg-[#F4F5F7] p-0 text-[#0F172A]">
          <DialogHeader className="border-b border-[#E3E7EC] bg-white px-6 py-4 text-left">
            <div className="flex items-center justify-between gap-3">
              <div>
                <DialogTitle className="text-lg">Journey for "{results?.query}"</DialogTitle>
                <DialogDescription className="text-[#64748B]">
                  {results?.count} reference group{results?.count === 1 ? "" : "s"} · {results?.source === "po" ? "full pipeline from the PO Query" : "assembled across uploaded visibility snapshots"}.
                </DialogDescription>
              </div>
              <button
                data-testid="finder-export-dialog"
                onClick={doExport}
                disabled={exporting}
                className="flex shrink-0 items-center gap-2 rounded-sm border border-[#CAD1DB] bg-white px-4 py-2 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-[#EDEFF3] disabled:opacity-50"
              >
                {exporting ? <Spinner size={16} className="animate-spin" /> : <DownloadSimple size={16} weight="bold" />}
                Export Excel
              </button>
            </div>
          </DialogHeader>
          <div className="max-h-[calc(85vh-92px)] space-y-3 overflow-y-auto p-4">
            {results?.matches.map((m, i) => (
              <div key={m.reference2 || `noref-${i}`}>
                {m.matched_query && (
                  <div className="mb-1 font-mono text-[10px] text-[#94A3B8]">matched: {m.matched_query}</div>
                )}
                <ChainCard m={m} order={order} />
                {m.reference2 && (
                  <button
                    data-testid={`finder-open-drawer-${i}`}
                    onClick={() => { onOpenRef2(m.reference2); setOpen(false); }}
                    className="mt-1 text-xs font-medium text-[#F0323E] hover:underline"
                  >
                    Open visibility line items for this Reference 2 →
                  </button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
